import { computed, ref, type ComputedRef, type Ref, type ShallowRef } from 'vue';
import type {
  RevolveCutPreviewFragment,
  RevolveFeatureEditDefinition,
} from '@puppycad/cad-core';
import { cloneRevolveCutKeepShapeKeys } from './revolve-definition-snapshot';

/**
 * 旋转切除保留实体弹窗状态快照。
 */
export interface RevolveCutKeepBodiesDialogState {
  /** 弹窗是否可见。 */
  visible: boolean;
  /** 当前保留模式。 */
  selectionMode: 'all' | 'selected';
  /** 弹窗内尚未提交的保留片段选择。 */
  draftSelectedShapeKeys: Record<string, string[]>;
}

/**
 * 旋转切除可保留片段 UI 数据。
 */
export type RevolveCutKeepBodiesFragment = RevolveCutPreviewFragment & {
  /** 当前是否保留。 */
  selected: boolean;
};

/**
 * 旋转切除保留实体控制器参数。
 */
export interface RevolveCutKeepBodiesControllerOptions {
  /** 当前旋转定义。 */
  definition: ShallowRef<RevolveFeatureEditDefinition | null>;
  /** 当前预览拆分出的可保留片段。 */
  cutFragments: Ref<RevolveCutKeepBodiesFragment[]>;
  /** 确认弹窗后继续提交的回调。 */
  submit: () => void;
  /** 同步弹窗草稿选择到三维高亮层。 */
  previewSelection?: (selection: Record<string, string[]>) => void;
  /** 清理三维高亮层。 */
  clearPreviewSelection?: () => void;
}

/**
 * 旋转切除保留实体控制器。
 */
export interface RevolveCutKeepBodiesController {
  /** PropertyManager 消费的弹窗状态。 */
  dialogState: ComputedRef<RevolveCutKeepBodiesDialogState>;
  /** 判断当前提交是否已经由弹窗确认放行。 */
  isSubmitArmed: () => boolean;
  /** 重置提交放行状态。 */
  resetSubmitArmed: () => void;
  /** 清理弹窗状态。 */
  clear: () => void;
  /** 打开弹窗。 */
  open: () => void;
  /** 设置保留模式。 */
  setSelectionMode: (mode: 'all' | 'selected') => void;
  /** 切换弹窗内的草稿片段选择。 */
  toggleDraftFragment: (sourceSolidId: string, shapeKey: string) => void;
  /** 取消弹窗。 */
  cancel: () => void;
  /** 确认弹窗并继续提交。 */
  confirm: () => void;
}

/**
 * 创建旋转切除保留实体控制器。
 *
 * 设计说明：
 * 1. 保留实体弹窗只服务旋转切除，不能继续堆在旋转主会话 Hook 中；
 * 2. 控制器只处理 UI 草稿与提交放行，不负责生成 preview 或 commit 输入；
 * 3. 所有片段选择都保持为普通对象，避免把任何渲染对象放入 Vue 响应式状态。
 *
 * @param options 控制器参数。
 * @returns 保留实体控制器。
 */
export function useRevolveCutKeepBodiesController(
  options: RevolveCutKeepBodiesControllerOptions,
): RevolveCutKeepBodiesController {
  const visible = ref(false);
  const selectionMode = ref<'all' | 'selected'>('all');
  const draftSelectedShapeKeys = ref<Record<string, string[]>>({});
  let submitArmed = false;

  /**
   * 构建当前全部可保留实体片段键。
   *
   * @returns 按源实体分组的片段键。
   */
  const buildAllKeepShapeKeys = (): Record<string, string[]> => {
    const grouped = new Map<string, string[]>();
    options.cutFragments.value.forEach((fragment) => {
      const shapeKeys = grouped.get(fragment.sourceSolidId) ?? [];
      shapeKeys.push(fragment.shapeKey);
      grouped.set(fragment.sourceSolidId, shapeKeys);
    });
    return Object.fromEntries(grouped);
  };

  /**
   * 同步当前弹窗草稿选择到三维高亮层。
   */
  const syncPreviewSelection = (): void => {
    const selection = selectionMode.value === 'all'
      ? buildAllKeepShapeKeys()
      : cloneRevolveCutKeepShapeKeys(draftSelectedShapeKeys.value);
    options.previewSelection?.(selection);
  };

  /**
   * 清理三维高亮层。
   */
  const clearPreviewSelection = (): void => {
    options.clearPreviewSelection?.();
  };

  /**
   * 弹窗状态投影。
   */
  const dialogState = computed<RevolveCutKeepBodiesDialogState>(() => ({
    visible: visible.value,
    selectionMode: selectionMode.value,
    draftSelectedShapeKeys: cloneRevolveCutKeepShapeKeys(draftSelectedShapeKeys.value),
  }));

  /**
   * 判断当前提交是否已放行。
   *
   * @returns 是否已由弹窗确认放行。
   */
  const isSubmitArmed = (): boolean => submitArmed;

  /**
   * 重置提交放行状态。
   */
  const resetSubmitArmed = (): void => {
    submitArmed = false;
  };

  /**
   * 清理弹窗状态。
   */
  const clear = (): void => {
    visible.value = false;
    draftSelectedShapeKeys.value = {};
    submitArmed = false;
    clearPreviewSelection();
  };

  /**
   * 打开保留实体弹窗。
   */
  const open = (): void => {
    selectionMode.value = 'all';
    draftSelectedShapeKeys.value = cloneRevolveCutKeepShapeKeys(
      options.definition.value?.keepShapeKeys ?? buildAllKeepShapeKeys(),
    );
    visible.value = true;
    syncPreviewSelection();
  };

  /**
   * 设置保留实体选择模式。
   *
   * @param mode 选择模式。
   */
  const setSelectionMode = (mode: 'all' | 'selected'): void => {
    selectionMode.value = mode;
    syncPreviewSelection();
  };

  /**
   * 切换弹窗中的草稿片段选择。
   *
   * @param sourceSolidId 来源实体 ID。
   * @param shapeKey 片段稳定键。
   */
  const toggleDraftFragment = (sourceSolidId: string, shapeKey: string): void => {
    const nextSelection = cloneRevolveCutKeepShapeKeys(draftSelectedShapeKeys.value);
    const current = new Set(nextSelection[sourceSolidId] ?? []);
    if (current.has(shapeKey)) {
      current.delete(shapeKey);
    } else {
      current.add(shapeKey);
    }
    nextSelection[sourceSolidId] = Array.from(current);
    draftSelectedShapeKeys.value = nextSelection;
    syncPreviewSelection();
  };

  /**
   * 取消保留实体弹窗。
   */
  const cancel = (): void => {
    visible.value = false;
    submitArmed = false;
    clearPreviewSelection();
  };

  /**
   * 确认保留实体弹窗并继续提交。
   */
  const confirm = (): void => {
    if (!options.definition.value) {
      return;
    }
    const keepShapeKeys = selectionMode.value === 'all'
      ? buildAllKeepShapeKeys()
      : cloneRevolveCutKeepShapeKeys(draftSelectedShapeKeys.value);
    options.definition.value = {
      ...options.definition.value,
      keepShapeKeys,
    };
    submitArmed = true;
    visible.value = false;
    clearPreviewSelection();
    options.submit();
  };

  return {
    dialogState,
    isSubmitArmed,
    resetSubmitArmed,
    clear,
    open,
    setSelectionMode,
    toggleDraftFragment,
    cancel,
    confirm,
  };
}
